import pmDecisionService from '@/services/pmDecisionService';
import {
  filterDecisions,
  normalizeDecisionRow,
  sortDecisionsForList,
} from '@/utils/decisionDisplay';

export default {
  namespaced: true,

  state: {
    items: [],
    loading: false,
    error: null,
    statusFilter: 'all',
    deciderFilter: '',
    projectFilter: '',
    milestoneFilter: '',
    dueFilter: '',
    escalationFilter: '',
    selectedId: null,
  },

  getters: {
    items: (state) => state.items,
    loading: (state) => state.loading,
    error: (state) => state.error,
    statusFilter: (state) => state.statusFilter,
    deciderFilter: (state) => state.deciderFilter,
    projectFilter: (state) => state.projectFilter,
    milestoneFilter: (state) => state.milestoneFilter,
    dueFilter: (state) => state.dueFilter,
    escalationFilter: (state) => state.escalationFilter,
    selectedId: (state) => state.selectedId,
    filteredDecisions(state) {
      const filtered = filterDecisions(state.items, {
        statusFilter: state.statusFilter,
        deciderFilter: state.deciderFilter,
        projectFilter: state.projectFilter,
        milestoneFilter: state.milestoneFilter,
        dueFilter: state.dueFilter,
        escalationFilter: state.escalationFilter,
      });
      return sortDecisionsForList(filtered);
    },
    selectedDecision(state, getters) {
      if (!getters.filteredDecisions.length) return null;
      const selected = getters.filteredDecisions.find((item) => item.id === state.selectedId);
      return selected ?? getters.filteredDecisions[0];
    },
    isEmpty: (state, getters) => !state.loading && !state.error && getters.filteredDecisions.length === 0,
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_ITEMS(state, items) {
      state.items = items || [];
    },
    SET_STATUS_FILTER(state, statusFilter) {
      state.statusFilter = statusFilter || 'all';
    },
    SET_DECIDER_FILTER(state, deciderFilter) {
      state.deciderFilter = deciderFilter || '';
    },
    SET_PROJECT_FILTER(state, projectFilter) {
      state.projectFilter = projectFilter || '';
    },
    SET_MILESTONE_FILTER(state, milestoneFilter) {
      state.milestoneFilter = milestoneFilter || '';
    },
    SET_DUE_FILTER(state, dueFilter) {
      state.dueFilter = dueFilter || '';
    },
    SET_ESCALATION_FILTER(state, escalationFilter) {
      state.escalationFilter = escalationFilter || '';
    },
    SET_SELECTED_ID(state, selectedId) {
      state.selectedId = selectedId || null;
    },
    UPDATE_ITEM(state, updated) {
      const index = state.items.findIndex((item) => item.id === updated.id);
      if (index !== -1) {
        state.items.splice(index, 1, { ...state.items[index], ...updated });
      }
    },
    REMOVE_ITEM(state, id) {
      state.items = state.items.filter((item) => item.id !== id);
      if (state.selectedId === id) state.selectedId = null;
    },
    RESET(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.statusFilter = 'all';
      state.deciderFilter = '';
      state.projectFilter = '';
      state.milestoneFilter = '';
      state.dueFilter = '';
      state.escalationFilter = '';
      state.selectedId = null;
    },
  },

  actions: {
    setStatusFilter({ commit }, value) {
      commit('SET_STATUS_FILTER', value);
    },
    setDeciderFilter({ commit }, value) {
      commit('SET_DECIDER_FILTER', value);
    },
    setProjectFilter({ commit }, value) {
      commit('SET_PROJECT_FILTER', value);
    },
    setMilestoneFilter({ commit }, value) {
      commit('SET_MILESTONE_FILTER', value);
    },
    setDueFilter({ commit }, value) {
      commit('SET_DUE_FILTER', value);
    },
    setEscalationFilter({ commit }, value) {
      commit('SET_ESCALATION_FILTER', value);
    },
    selectDecision({ commit }, id) {
      commit('SET_SELECTED_ID', id);
    },

    async fetchPortfolioDecisions({ commit, rootGetters }) {
      const projects = rootGetters['pmProject/activeProjects'] || [];
      const contextReady = rootGetters['pmProject/userProjectContextReady'];

      if (projects.length === 0) {
        if (!contextReady) {
          return [];
        }
        commit('SET_ITEMS', []);
        return [];
      }

      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const results = await Promise.allSettled(
          projects.map(async (project) => {
            const data = await pmDecisionService.listDecisions(project.id);
            const items = data?.items || [];
            return items.map((item) => normalizeDecisionRow(item, {
              projectId: project.id,
              projectName: project.name,
            }));
          }),
        );

        const merged = results
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value);

        if (merged.length === 0 && results.some((result) => result.status === 'rejected')) {
          throw new Error('Failed to load decisions');
        }

        commit('SET_ITEMS', merged);
        return merged;
      } catch (error) {
        const message = error?.message || 'Failed to load decisions';
        commit('SET_ERROR', message);
        commit('SET_ITEMS', []);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async refreshDecisionItem({ commit }, { projectId, id }) {
      const data = await pmDecisionService.getDecision(projectId, id);
      commit('UPDATE_ITEM', normalizeDecisionRow(data, { projectId }));
      return data;
    },

    patchDecisionItem({ commit }, item) {
      commit('UPDATE_ITEM', item);
    },

    removeDecisionItem({ commit }, id) {
      commit('REMOVE_ITEM', id);
    },
  },
};
